import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../QueryKeys';

// Tool call tracking state interface
export interface ToolCallCard {
  id: string;
  type: 'create_itinerary' | 'add_day' | 'add_stop' | 'add_leg' | 'web_browser' | 'update_itinerary' | 'update_stop' | 'lookup_location' | 'optimize_route' | 'google_search';
  status: 'loading' | 'complete';
  data: any;
}

// Helper functions for parsing tool call data
const parseToolCallResult = (toolCall: any) => {
  try {
    if (typeof toolCall.result === 'string') {
      return JSON.parse(toolCall.result);
    }
    return toolCall.result;
  } catch {
    return null;
  }
};

const extractToolCallData = (toolCall: any) => {
  const args = typeof toolCall.args?.input === 'string' 
    ? JSON.parse(toolCall.args.input) 
    : toolCall.args?.input || {};
  
  const result = parseToolCallResult(toolCall);
  
  switch (toolCall.toolName) {
    case 'create_itinerary':
      return {
        type: 'create_itinerary' as const,
        data: {
          title: args.title || result?.title,
          shortDescription: args.shortDescription || result?.shortDescription,
          imageUrl: result?.imageUrl,
          itineraryId: result?.id, // Include the created itinerary ID
        }
      };
    
    case 'add_day':
      return {
        type: 'add_day' as const,
        data: {
          title: args.title || result?.title,
          description: args.description || result?.description,
          dayNumber: result?.number,
          itineraryImageUrl: result?.itinerary?.imageUrl,
        }
      };
    
    case 'add_stop':
      return {
        type: 'add_stop' as const,
        data: {
          title: args.name || result?.title,
          highlight: args.highlights || result?.highlight,
          description: args.description || result?.description,
          imageUrl: result?.imageUrl,
          index: result?.index,
        }
      };
    
    case 'add_leg':
      return {
        type: 'add_leg' as const,
        data: {
          fromStopId: args.fromStopId,
          toStopId: args.toStopId,
          travelMode: args.travelMode || result?.travelMode,
          travelTimeMinutes: args.travelTimeMinutes || result?.travelTimeMinutes,
          distanceMeters: args.distanceMeters || result?.distanceMeters,
          notes: args.notes || result?.notes,
          fromStopTitle: result?.fromStop?.title,
          toStopTitle: result?.toStop?.title,
        }
      };
    
    case 'web_browser':
      return {
        type: 'web_browser' as const,
        data: {
          url: args.url,
          query: args.query,
          title: args.url ? new URL(args.url).hostname : 'Website',
          description: args.query || 'Browsing website for information',
          result: result,
        }
      };
    
    case 'update_itinerary':
      return {
        type: 'update_itinerary' as const,
        data: {
          title: args.title || result?.title,
          shortDescription: args.shortDescription || result?.shortDescription,
          description: args.description || result?.description,
          imageUrl: result?.imageUrl,
          visibility: args.visibility,
        }
      };
    
    case 'update_stop':
      return {
        type: 'update_stop' as const,
        data: {
          title: args.title || result?.title,
          highlight: args.highlight || result?.highlight,
          description: args.description || result?.description,
          imageUrl: args.imageUrl || result?.imageUrl,
          state: args.state,
        }
      };
    
    case 'lookup_location':
      return {
        type: 'lookup_location' as const,
        data: {
          query: args.query,
          city: args.city,
          country: args.country,
          title: args.query,
          description: `Searching for location: ${args.query}${args.city ? ` in ${args.city}` : ''}`,
          result: result,
        }
      };
    
    case 'optimize_route':
      return {
        type: 'optimize_route' as const,
        data: {
          itineraryId: args.itineraryId,
          dayId: args.dayId,
          title: 'Route Optimization',
          description: 'Optimizing stop order for efficient travel',
          result: result,
        }
      };
    
    case 'google_search':
      return {
        type: 'google_search' as const,
        data: {
          query: args.query,
          location: args.location,
          title: 'Web Search',
          description: `Searching for: ${args.query}${args.location ? ` in ${args.location}` : ''}`,
          result: result,
        }
      };
    
    default:
      return null;
  }
};

export function useToolCallHandling() {
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [allToolCalls, setAllToolCalls] = useState<ToolCallCard[]>([]);
  const [createdItinerary, setCreatedItinerary] = useState<any>(null);

  // Get the most recent tool call (topmost in stack)
  const currentToolCall = allToolCalls.length > 0 
    ? allToolCalls[allToolCalls.length - 1]
    : null;

  const addOrUpdateToolCall = useCallback((toolCall: any) => {
    const extractedData = extractToolCallData(toolCall);
    if (!extractedData) return;

    const newCard: ToolCallCard = {
      id: toolCall.toolCallId,
      type: extractedData.type,
      status: toolCall.status === 'complete' ? 'complete' : 'loading',
      data: extractedData.data,
    };

    setAllToolCalls(prev => {
      const existingIndex = prev.findIndex(tc => tc.id === toolCall.toolCallId);
      if (existingIndex >= 0) {
        // Update existing card
        const updated = [...prev];
        updated[existingIndex] = newCard;
        return updated;
      } else {
        // Add new card
        return [...prev, newCard];
      }
    });
  }, []);

  const clearToolCalls = useCallback(() => {
    setAllToolCalls([]);
    setCreatedItinerary(null);
  }, []);

  // Helper to invalidate relevant queries when tool calls complete
  const invalidateQueriesForToolCall = useCallback((toolCall: any) => {
    if (toolCall.status !== 'complete') return;
    
    const result = parseToolCallResult(toolCall);
    
    // Get current itinerary ID from URL for targeted invalidation
    const currentPath = window.location.pathname;
    const itineraryIdMatch = currentPath.match(/\/itineraries\/([0-9a-f-]{36})/i);
    const currentItineraryId = itineraryIdMatch ? itineraryIdMatch[1] : null;
    
    // Invalidate based on tool call type
    switch (toolCall.toolName) {
      case 'create_itinerary':
        // When itinerary is created, invalidate general itinerary queries
        queryClient.invalidateQueries({
          predicate: (query) => {
            const keyStr = JSON.stringify(query.queryKey);
            return keyStr.includes('itinerary') || keyStr.includes('account');
          }
        });
        break;
        
      case 'add_day':
        // When day is added, invalidate itinerary-specific queries
        if (currentItineraryId) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.days(currentItineraryId)
          });
        }
        break;
        
      case 'add_stop':
        // When stop is added, invalidate itinerary and day-specific queries
        if (currentItineraryId) {
          // Invalidate days query (which includes stops)
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.days(currentItineraryId)
          });
          // Invalidate any specific stop queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              const keyStr = JSON.stringify(query.queryKey);
              return keyStr.includes('stops') && keyStr.includes(currentItineraryId);
            }
          });
        }
        break;
        
      case 'add_leg':
        // When leg is added, invalidate leg and itinerary queries
        if (currentItineraryId) {
          // Invalidate legs query
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.legs(currentItineraryId)
          });
          // Invalidate days query (which may include leg data)
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.days(currentItineraryId)
          });
        }
        break;
        
      case 'update_itinerary':
        // When itinerary is updated, invalidate itinerary-specific queries
        if (currentItineraryId) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.details(currentItineraryId)
          });
        }
        break;
        
      case 'update_stop':
        // When stop is updated, invalidate stop and itinerary queries
        if (currentItineraryId) {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const keyStr = JSON.stringify(query.queryKey);
              return keyStr.includes('stops') && keyStr.includes(currentItineraryId);
            }
          });
        }
        break;
        
      case 'optimize_route':
        // When route is optimized, invalidate day and stop queries
        if (currentItineraryId) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.itinerary.days(currentItineraryId)
          });
          queryClient.invalidateQueries({
            predicate: (query) => {
              const keyStr = JSON.stringify(query.queryKey);
              return keyStr.includes('stops') && keyStr.includes(currentItineraryId);
            }
          });
        }
        break;
    }
  }, [queryClient]);

  const handleToolCall = useCallback(async ({ toolCall }: { toolCall: any }) => {
    // Add or update tool call card
    addOrUpdateToolCall(toolCall);
    
    // Invalidate relevant queries when tool call completes (for live map updates)
    invalidateQueriesForToolCall(toolCall);
    
    // Handle itinerary creation tool calls when result is available  
    if (toolCall.toolName === 'create_itinerary' && 
        (toolCall as any).status === 'complete' && 
        (toolCall as any).result) {
      
      // Parse the result to extract the itinerary ID
      const result = parseToolCallResult(toolCall);
      const itineraryId = result?.id;
      const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
      
      if (itineraryId && uuidRegex.test(itineraryId)) {
        // Store the created itinerary data for the summary card and map
        setCreatedItinerary({
          ...result,
          id: itineraryId,
          dayCount: result?.days?.length || 0,
          stopCount: result?.days?.reduce((total: number, day: any) => total + (day?.stops?.length || 0), 0) || 0,
        });

        // Check if we're already on this itinerary's page
        const currentPath = window.location.pathname;
        const isAlreadyOnItinerary = currentPath.includes(`/itineraries/${itineraryId}`);
        
        if (!isAlreadyOnItinerary) {
          // Prepare navigation parameters preserving chat state and threadId
          const searchParams = new URLSearchParams(window.location.search);
          
          // Preserve chat open state
          if (searchParams.get('chatOpen')) {
            searchParams.set('chatOpen', 'true');
          }
          
          // Preserve threadId if it exists and is not 'new'
          const currentThreadId = searchParams.get('chatThreadId');
          if (currentThreadId && currentThreadId !== 'new') {
            searchParams.set('chatThreadId', currentThreadId);
          }

          // Navigate immediately when itinerary creation is complete with hash to scroll to map
          navigate({
            to: `/itineraries/${itineraryId}`,
            search: Object.fromEntries(searchParams.entries()),
            hash: 'itinerary-map',
            resetScroll: false
          });

          toast.success(t('common.chat.itineraryCreated'));
        }
      }
    }
    
    // Smart navigation logic for canvas
    if (toolCall.status === 'complete') {
      // Use window.location.pathname instead of routerState for more reliable path detection
      const currentPath = window.location.pathname;
      const isOnCanvas = currentPath.includes('/itineraries/');
      
      if (isOnCanvas) {
        // Extract itinerary ID from current path to ensure we're on the right itinerary
        const itineraryIdMatch = currentPath.match(/\/itineraries\/([0-9a-f-]{36})/i);
        const currentItineraryId = itineraryIdMatch ? itineraryIdMatch[1] : null;
        
        // Only navigate if we have a valid itinerary ID
        if (currentItineraryId) {
          const currentSearch = window.location.search ? Object.fromEntries(new URLSearchParams(window.location.search)) : {};
          const navigationUpdate: { tab?: string; day?: number } = {};
          
          // Navigate based on tool call type
        switch (toolCall.toolName) {
          case 'add_day': {
            navigationUpdate.tab = 'planner';
            // Extract day number from result (the created day object)
            const dayResult = parseToolCallResult(toolCall);
            if (dayResult?.number) {
              navigationUpdate.day = dayResult.number;
            }
            break;
          }
            
          case 'add_stop': {
            navigationUpdate.tab = 'planner';
            // Extract day number from args (more reliable than parsing result)
            const stopArgs = typeof toolCall.args?.input === 'string' 
              ? JSON.parse(toolCall.args.input) 
              : toolCall.args?.input || toolCall.args || {};
            if (stopArgs.dayNumber) {
              navigationUpdate.day = stopArgs.dayNumber;
            }
            break;
          }
            
          case 'update_itinerary':
            navigationUpdate.tab = 'overview';
            break;
        }
        
                            // Apply navigation update if needed
          if (Object.keys(navigationUpdate).length > 0) {
            const newSearch = { 
              ...currentSearch,
              ...navigationUpdate
            };
            
            const currentSearchStr = JSON.stringify(currentSearch);
            const newSearchStr = JSON.stringify(newSearch);
            if (currentSearchStr !== newSearchStr) {
              setTimeout(() => {
                navigate({
                  to: window.location.pathname,
                  search: newSearch,
                  replace: true,
                  resetScroll: false
                });
                
                // Add scrolling behavior after navigation
                setTimeout(() => {
                  if (toolCall.toolName === 'add_stop') {
                    // Scroll to top when stop is added
                    console.log('🎯 Scrolling to top after stop creation');
                    window.scrollTo({ 
                      top: 0, 
                      behavior: 'smooth' 
                    });
                  }
                }, 100); // Small delay to ensure navigation is complete
              }, 500);
            }
          }
        }
      }
    }
  }, [addOrUpdateToolCall, invalidateQueriesForToolCall, t, navigate]);

  return {
    activeToolCalls: allToolCalls,
    currentToolCall,
    handleToolCall,
    clearToolCalls,
    createdItinerary,
  };
} 